// Delivery Section component
import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaSyncAlt, FaTruck } from "react-icons/fa";
import type { CartTheme } from "../types";
import type { DeliveryZone } from "~/types/new_types";
import LocationButton from "~/components/ui/LocationButton";
import LocationSuccess from "~/components/ui/LocationSuccess";
import MapContainer from "~/components/ui/MapContainer";

interface DeliverySectionProps {
    manualAddress: string;
    setManualAddress: (value: string) => void;
    userLocation: string;
    locationError: string;
    isGettingLocation: boolean;
    getLocation: () => void;
    branch?: {
        location?: any;
        name?: string;
        logo?: string | null;
    } | null;
    theme?: CartTheme | null;
    // Delivery zones
    deliveryZones?: DeliveryZone[];
    selectedZone: DeliveryZone | null;
    setSelectedZone: (zone: DeliveryZone | null) => void;
    formatPrice: (price: number) => string;
}

export const DeliverySection: React.FC<DeliverySectionProps> = ({
    manualAddress,
    setManualAddress,
    userLocation,
    locationError,
    isGettingLocation,
    getLocation,
    branch,
    theme,
    deliveryZones,
    selectedZone,
    setSelectedZone,
    formatPrice,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "ar";

    const resolvedPrimaryColor = theme?.primaryColor || "#ea580c";
    const resolvedBorderColor = theme?.borderColor || "#d1d5db";
    const resolvedBackgroundColor = theme?.backgroundColor || "#ffffff";
    const resolvedTextColor = theme?.textColor || "#1f2937";

    return (
        <div className="space-y-6 pt-4">
            {/* Delivery Zone Selection */}
            {deliveryZones && deliveryZones.length > 0 && (
                <div>
                    <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: resolvedTextColor }}
                    >
                        <span className="flex items-center gap-2">
                            <FaTruck className="text-sm" style={{ color: resolvedPrimaryColor }} />
                            {t("menu.cart.deliveryZone")}
                        </span>
                    </label>
                    <select
                        value={selectedZone?.id || ""}
                        onChange={(e) => {
                            const zone = deliveryZones.find(z => z.id === e.target.value);
                            setSelectedZone(zone || null);
                        }}
                        dir={isRTL ? "rtl" : "ltr"}
                        className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:border-transparent transition-all outline-none cursor-pointer appearance-none"
                        style={{
                            color: resolvedTextColor,
                            backgroundColor: resolvedBackgroundColor,
                            borderColor: selectedZone ? resolvedPrimaryColor : resolvedBorderColor,
                            textAlign: isRTL ? "right" : "left",
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = resolvedPrimaryColor;
                            e.target.style.boxShadow = `0 0 0 3px ${resolvedPrimaryColor}30`;
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = selectedZone ? resolvedPrimaryColor : resolvedBorderColor;
                            e.target.style.boxShadow = "none";
                        }}
                    >
                        <option value="">{t("menu.cart.selectZone")}</option>
                        {deliveryZones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                                {isRTL ? zone.ar_zone_name : zone.zone_name} - {formatPrice(zone.fee)}
                            </option>
                        ))}
                    </select>
                    {selectedZone && (
                        <p 
                            className="mt-2 text-sm flex items-center gap-1"
                            style={{ color: resolvedPrimaryColor }}
                        >
                            {t("menu.cart.deliveryFee")}: {formatPrice(selectedZone.fee)}
                        </p>
                    )}
                </div>
            )}

            <label
                className="block text-sm font-medium mb-1"
                style={{ color: resolvedTextColor }}
            >
                {t("menu.cart.deliveryLocation")} *
            </label>

            <input
                type="text"
                required
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                placeholder={t("menu.cart.addressPlaceholder")}
                dir={isRTL ? "rtl" : "ltr"}
                autoComplete="street-address"
                name="address"
                className="w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:border-transparent mb-3 transition-all outline-none"
                style={{
                    color: resolvedTextColor,
                    backgroundColor: resolvedBackgroundColor,
                    borderColor: resolvedBorderColor,
                    textAlign: isRTL ? "right" : "left",
                }}
                onFocus={(e) => {
                    e.target.style.borderColor = resolvedPrimaryColor;
                    e.target.style.boxShadow = `0 0 0 3px ${resolvedPrimaryColor}30`;
                    e.target.style.backgroundColor = `${resolvedPrimaryColor}08`;
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = resolvedBorderColor;
                    e.target.style.boxShadow = "none";
                    e.target.style.backgroundColor = resolvedBackgroundColor;
                }}
            />

            <div className="text-center">
                <span
                    className="text-sm mb-3 block"
                    style={{ color: resolvedTextColor }}
                >
                    {t("menu.cart.and")}
                </span>

                {!userLocation && (
                    <LocationButton
                        onClick={getLocation}
                        isLoading={isGettingLocation}
                        theme={{
                            primaryColor: resolvedPrimaryColor,
                            textColor: resolvedTextColor,
                            backgroundColor: resolvedBackgroundColor,
                        }}
                        loadingText={t("menu.cart.location.getting")}
                        buttonText={t("menu.cart.location.get")}
                        className="w-full"
                    />
                )}
            </div>

            {userLocation && (
                <LocationSuccess
                    message={t("menu.cart.location.detected")}
                    theme={{
                        primaryColor: resolvedPrimaryColor,
                        textColor: resolvedTextColor,
                        backgroundColor: resolvedBackgroundColor,
                    }}
                    className="mt-4"
                />
            )}

            {locationError && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 p-3 rounded-lg border border-red-300 bg-red-50"
                >
                    <p className="text-red-600 text-sm flex items-center gap-2">
                        <span>⚠️</span>
                        {locationError}
                    </p>
                </motion.div>
            )}

            {/* Embedded Location Map for Delivery */}
            {branch?.location && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                        <span
                            className="text-sm font-medium"
                            style={{ color: resolvedTextColor }}
                        >
                            {t("menu.cart.deliveryLocation")}
                        </span>
                        <button
                            onClick={getLocation}
                            disabled={isGettingLocation}
                            className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-all hover:opacity-80 disabled:opacity-50"
                            style={{
                                color: resolvedPrimaryColor,
                                backgroundColor: `${resolvedPrimaryColor}15`,
                            }}
                        >
                            <FaSyncAlt
                                size={10}
                                className={isGettingLocation ? "animate-spin" : ""}
                            />
                            {isGettingLocation
                                ? t("menu.cart.location.getting")
                                : userLocation
                                    ? t("menu.cart.location.refresh")
                                    : t("menu.cart.location.get")}
                        </button>
                    </div>
                    <MapContainer
                        restaurantLocation={branch?.location}
                        userLocation={userLocation}
                        restaurantName={branch?.name}
                        restaurantLogo={branch?.logo ?? undefined}
                        placeId={branch?.location?.place_id}
                        theme={{
                            primaryColor: resolvedPrimaryColor,
                            textColor: resolvedTextColor,
                            backgroundColor: resolvedBackgroundColor,
                        }}
                        title={""}
                        subtitle={
                            userLocation && branch?.location
                                ? t("menu.cart.location.routeToYourLocation")
                                : t("menu.cart.location.locationDetails")
                        }
                        showForPickup={false}
                        onGetUserLocation={getLocation}
                    />
                </div>
            )}
        </div>
    );
};

export default DeliverySection;
